/** Дрібні спільні елементи інтерфейсу. */

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useUi } from '../store/ui.js';
import { Segments } from './Segments.js';

/**
 * Що закриває верх екрана: шапка, а на сторінці таблиці — ще й рядок її кнопок.
 * Сповіщення стають нижче за найнижчий із них.
 */
function topClear(): number {
  let bottom = 0;
  for (const el of document.querySelectorAll<HTMLElement>('.app-header, .toolbar')) {
    const r = el.getBoundingClientRect();
    // Рядок кнопок, що поїхав униз разом зі сторінкою, сповіщенням не заважає.
    if (r.height && r.top < window.innerHeight / 3) bottom = Math.max(bottom, r.bottom);
  }
  return bottom;
}

/**
 * Стос сповіщень угорі праворуч, під шапкою й кнопками таблиці. `children` —
 * постійні сповіщення, що висять, доки їх не закриють: стоять над короткими
 * «Збережено», щоб ті не стрибали.
 */
export function Toasts({ children }: { children?: ReactNode }): ReactNode {
  const toasts = useUi(s => s.toasts);
  const dismiss = useUi(s => s.dismiss);
  const ref = useRef<HTMLDivElement>(null);

  // Місце міряємо, коли сповіщення з'являються: кнопки таблиці є не на кожній сторінці.
  useLayoutEffect(() => {
    const place = (): void => {
      const bottom = topClear();
      if (ref.current) ref.current.style.top = bottom ? `${Math.round(bottom) + 8}px` : '';
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [toasts.length, children]);

  return (
    <div className="toasts" ref={ref}>
      {children}
      {toasts.map(t => (
        <div
          key={t.id}
          className={[
            'toast',
            t.kind === 'err' ? 'err' : t.kind === 'ok' ? 'ok' : '',
            t.leaving ? 'leaving' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <span className="toast-text">{t.text}</span>
          <button
            type="button"
            className="toast-close"
            aria-label="Закрити"
            onClick={() => dismiss(t.id)}
          >
            <CloseIcon />
          </button>
        </div>
      ))}
    </div>
  );
}

export interface ModalProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  /**
   * Стала висота вікна. Потрібна там, де всередині перемикають вкладки: у
   * вкладок різна кількість полів, і без неї вікно підстрибує на кожному
   * перемиканні — кнопки в підвалі тікають з-під курсора, а вміст під ними
   * доводиться шукати очима заново.
   *
   * Порожнього місця від сталої висоти не з'являється: тіло такого вікна
   * розтягується на всю висоту, і різницю між вкладками добирає те, чому
   * зайва висота лише на користь, — текстове поле.
   */
  height?: number;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  width = 720,
  height,
}: ModalProps): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /*
   * На телефоні вікно — аркуш знизу, і закривати його там звикли не хрестиком,
   * а тим самим рухом, яким відкрили: потягнути шапку вниз. Тягнемо сам аркуш,
   * а не заслону; відпустили далі за третину висоти або кинули швидко — вікно
   * закривається, ні — повертається на місце.
   */
  const drag = useRef<{ id: number; y0: number; t0: number } | null>(null);
  const [pull, setPull] = useState(0);

  const grabStart = (e: PointerEvent<HTMLElement>): void => {
    if (!matchMedia('(max-width: 860px)').matches) return;
    // Хрестик у шапці — кнопка, а не ручка.
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { id: e.pointerId, y0: e.clientY, t0: performance.now() };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const grabMove = (e: PointerEvent<HTMLElement>): void => {
    if (drag.current?.id !== e.pointerId) return;
    setPull(Math.max(0, e.clientY - drag.current.y0));
  };
  const grabEnd = (e: PointerEvent<HTMLElement>): void => {
    const d = drag.current;
    if (d?.id !== e.pointerId) return;
    drag.current = null;
    const dy = e.clientY - d.y0;
    const speed = dy / Math.max(1, performance.now() - d.t0);
    const far = dy > (ref.current?.offsetHeight ?? 400) / 3;
    setPull(0);
    if (far || speed > 0.6) onClose();
  };

  // Поза шарами сторінки: шапка застосунку не перекриває діалог.
  return createPortal(
    <div className="modal-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div
        className={`modal${height ? ' steady' : ''}${pull ? ' pulling' : ''}`}
        style={{
          maxWidth: width,
          height,
          ...(pull ? { transform: `translateY(${pull}px)` } : null),
        }}
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header
          onPointerDown={grabStart}
          onPointerMove={grabMove}
          onPointerUp={grabEnd}
          onPointerCancel={grabEnd}
        >
          {/* Ручка аркуша: видима лише на телефоні — там, де аркуш можна тягнути. */}
          <span className="modal-grip" aria-hidden="true" />
          <h2>{title}</h2>
          <CloseButton onClose={onClose} />
        </header>
        <div className="body">{children}</div>
        {footer ? <footer>{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export function Field({
  label,
  hint,
  children,
  style,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  /** Ширина поля в рядку: у .field-row елементи інакше не тягнуться. */
  style?: CSSProperties;
}): ReactNode {
  return (
    <div className="field" style={style}>
      <label>{label}</label>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </div>
  );
}

export function Spinner({ label }: { label?: string }): ReactNode {
  return (
    <div className="center-box">
      <div style={{ display: 'grid', gap: 10, justifyItems: 'center' }}>
        <div className="spinner" />
        {label ? <span className="muted">{label}</span> : null}
      </div>
    </div>
  );
}

export function Confirm({
  title,
  text,
  confirmLabel = 'Видалити',
  tone = 'danger',
  onConfirm,
  onClose,
}: {
  title: string;
  text: string;
  confirmLabel?: string;
  /**
   * Питають не лише перед видаленням. Дія буває просто помітною — увімкнути
   * розсилку всьому курсу, — і червона кнопка обіцяла б у ній втрату, якої
   * немає.
   */
  tone?: 'danger' | 'primary';
  onConfirm: () => void;
  onClose: () => void;
}): ReactNode {
  return (
    <Modal
      title={title}
      onClose={onClose}
      width={420}
      footer={
        <>
          <button
            className={`btn ${tone}`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      <p style={{ margin: 0 }}>{text}</p>
    </Modal>
  );
}

/**
 * Замок для панелі інструментів. Малюємо контуром, а не емодзі: емодзі
 * приходить із власним кольором і розміром, тому серед літер палітри виглядає
 * чужорідно й не слухається теми.
 */
/**
 * Замок позначок на телефоні: рука, що торкається екрана, перекреслена — поки
 * стоїть замок, тап нічого не змінює. Колодкою не малюємо: вона вже зайнята
 * замком минулих днів.
 */
export function TouchIcon({ locked }: { locked: boolean }): ReactNode {
  return (
    <svg className="ico-touch" viewBox="0 0 512 512" aria-hidden>
      <path
        transform="translate(85.333333, 42.666667)"
        fill="currentColor"
        d="M206.413257,215.447893 L206.413257,130.666667 C206.413257,99.7915733 181.28835,74.6666667 150.413257,74.6666667 C119.538163,74.6666667 94.4132566,99.7915733 94.4132566,130.666667 L94.4132566,250.096427 C84.0473899,241.401173 71.1085099,236.468907 57.5200299,236.468907 C41.3429632,236.468907 25.8116566,243.323093 14.9470166,255.281493 C-6.06205009,278.304853 -4.69501009,313.828267 17.6045099,335.429973 C17.6330966,335.45856 17.6435499,335.48992 17.6721366,335.518507 L112,426.666667 L320,426.666667 L320,250.666667 L206.413257,215.447893 Z M277.333333,384 C277.333333,384 128.393387,384 128,384 C128,384 53.2621099,313.552 45.9912832,306.927147 C37.7177899,299.39072 37.1202432,286.573013 44.6579499,278.29696 C52.1969366,270.023467 65.0146432,269.4272 73.2894166,276.963627 C78.8286166,282.010453 115.241417,312 115.241417,312 L131.74659,303.46624 L131.74659,130.666667 C131.74659,120.356693 140.104563,112 150.413257,112 C160.72323,112 169.079923,120.356693 169.079923,130.666667 L169.079923,248.541653 L277.333333,282.666667 L277.333333,384 Z M24.1438166,163.708373 C21.3728299,153.1328 19.7465899,142.098987 19.7465899,130.666667 C19.7465899,58.6250667 78.3612032,7.10542736e-15 150.413257,7.10542736e-15 C222.46531,7.10542736e-15 281.079923,58.6250667 281.079923,130.666667 C281.079923,142.098987 279.453683,153.1328 276.682697,163.708373 L236.211443,149.69792 C237.574643,143.552 238.413257,137.218773 238.413257,130.666667 C238.413257,82.1431467 198.936777,42.6666667 150.413257,42.6666667 C101.889737,42.6666667 62.4132566,82.1431467 62.4132566,130.666667 C62.4132566,137.218773 63.2518699,143.552 64.6150699,149.69792 L24.1438166,163.708373 Z"
      />
      {locked ? (
        <>
          {/* Товста лінія кольору кнопки — просвіт, без нього риска губиться
              в заливці долоні. Колір кнопки — її власна заливка, якщо вона є. */}
          <path
            d="M108 108 L404 404"
            stroke="var(--tool-bg, var(--panel))"
            strokeWidth="76"
            strokeLinecap="round"
            fill="none"
          />
          <path
            d="M108 108 L404 404"
            stroke="currentColor"
            strokeWidth="40"
            strokeLinecap="round"
            fill="none"
          />
        </>
      ) : null}
    </svg>
  );
}

export function LockIcon({ open }: { open: boolean }): ReactNode {
  return (
    <svg className="ico-lock" viewBox="0 0 16 16" aria-hidden>
      <rect x="3" y="7" width="10" height="7" rx="1.6" fill="currentColor" />
      <path
        d={open ? 'M5.5 7V4.6a2.5 2.5 0 0 1 5 0' : 'M5.5 7V4.6a2.5 2.5 0 0 1 5 0V7'}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Режим підказки: напівпрозорі позначки. Вимкнено — око перекреслене, увімкнено — відкрите. */
export function HintIcon({ on = false }: { on?: boolean }): ReactNode {
  return (
    <svg className="ico-hint" viewBox="0 0 16 16" aria-hidden>
      <path
        d="M1.6 8S4 3.5 8 3.5 14.4 8 14.4 8 12 12.5 8 12.5 1.6 8 1.6 8Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="8" cy="8" r="1.65" fill="currentColor" />
      {on ? null : (
        <path
          d="M3.2 12.8 12.8 3.2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/**
 * Дрібні значки шапки й кнопок. Усі — один штрих 1.8, 16 px, currentColor:
 * символи Unicode (☾, ✕, ☰) на різних системах різної товщини й висоти,
 * а тут вони мають стояти в одному ряду з шестернею.
 */
function Glyph({ children, className = 'ico-ui' }: { children: ReactNode; className?: string }): ReactNode {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/**
 * Кнопка закриття вікна — точна копія замка з панелі таблиці: кнопка в
 * скляному жолобі (`.tools`), а під курсором чи пальцем під нею виїжджає
 * червоний скляний повзунок, як у закритого замка.
 */
export function CloseButton({ onClose }: { onClose: () => void }): ReactNode {
  const [hot, setHot] = useState(false);
  return (
    <Segments
      className="tools modal-close"
      onPointerEnter={() => setHot(true)}
      onPointerLeave={() => setHot(false)}
    >
      <button
        type="button"
        className={`ico-btn${hot ? ' on' : ''}`}
        style={
          hot
            ? ({
                ['--tool-bg']: 'var(--danger)',
                ['--tool-fg']: '#fff',
              } as React.CSSProperties)
            : undefined
        }
        onFocus={() => setHot(true)}
        onBlur={() => setHot(false)}
        onClick={onClose}
        aria-label="Закрити"
      >
        <CloseIcon />
      </button>
    </Segments>
  );
}

/**
 * Хрестик закриття й видалення: товстіший за решту значків і трохи більший —
 * його мусять бачити одразу, а не шукати. Ледь помітно «дихає» (див. `.ico-close`
 * у стилях), щоб кнопка не губилась серед статичного інтерфейсу.
 */
export function CloseIcon(): ReactNode {
  return (
    <svg
      className="ico-close"
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.8"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

export function MenuIcon(): ReactNode {
  return (
    <Glyph>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </Glyph>
  );
}

export function SunIcon(): ReactNode {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.5v2.2M12 19.3v2.2M2.5 12h2.2M19.3 12h2.2M5.3 5.3l1.5 1.5M17.2 17.2l1.5 1.5M5.3 18.7l1.5-1.5M17.2 6.8l1.5-1.5" />
    </Glyph>
  );
}

export function MoonIcon(): ReactNode {
  return (
    <Glyph>
      <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
    </Glyph>
  );
}

/** Лупа в полі пошуку — одна й та сама в таблиці, людях і налаштуваннях. */
export function SearchIcon(): ReactNode {
  return (
    <svg
      className="ico"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

/**
 * Напис «Рапорт+» у шапці.
 *
 * Слово й плюс — два окремі зображення, накладені одне на одне: плюс час від
 * часу обертається, а зробити це можна лише з окремою картинкою. Прямокутник
 * плюса заданий у частках слова (`--plus-*` у `.brand-logo`), тому обидві
 * висоти напису — шапка й кабінет — сходяться самі, без другого набору чисел.
 */
export function HeaderLogo(): ReactNode {
  return (
    <span className="mark brand-logo">
      <img
        className="logo header-logo mark-word-dark"
        src="/brand-word.png?v=5"
        alt="Рапорт+"
        draggable={false}
      />
      <img
        className="logo header-logo mark-word-light"
        src="/brand-word-light.png?v=5"
        alt=""
        aria-hidden
        draggable={false}
      />
      <img className="mark-plus" src="/brand-plus.png?v=5" alt="" aria-hidden draggable={false} />
    </span>
  );
}

/**
 * Знак продукту на заставці й екрані входу: «Рапорт+» з тризубом.
 *
 * Розібраний так само, як напис у шапці: тризуб лишається в картинці зі
 * словом — обертається тільки плюс. Підкладки під знаком немає, тому слово
 * має два кольори, як і в шапці: біле на темній темі, чорне на світлій.
 */
export function Logo({ className }: { className?: string } = {}): ReactNode {
  return (
    <span className={className ? `mark product-mark ${className}` : 'mark product-mark'}>
      <img
        className="logo mark-word-dark"
        src="/mark-word.png?v=6"
        alt="Рапорт+"
        draggable={false}
      />
      <img
        className="logo mark-word-light"
        src="/mark-word-light.png?v=6"
        alt=""
        aria-hidden
        draggable={false}
      />
      <img className="mark-plus" src="/mark-plus.png?v=6" alt="" aria-hidden draggable={false} />
    </span>
  );
}

/**
 * Екран завантаження. Показується, поки застосунок піднімає сесію:
 * інакше на секунду видно порожню сторінку.
 */
/**
 * Екран запуску.
 *
 * `delayed` — для очікування, яке зазвичай триває один кадр: сторінку, код
 * якої вже в пам'яті, заставка встигла б лише блимнути на весь екран, і це
 * читалося б як ривок переходу. Тому перші чверть секунди її не видно.
 */
export function Splash({
  note,
  delayed,
}: {
  org?: string;
  note?: string;
  delayed?: boolean;
}): ReactNode {
  return (
    <div className={`splash${delayed ? ' late' : ''}`} role="status" aria-label="Завантаження">
      <div className="splash-inner">
        <Logo className="is-busy" />
        {/* Опис програми зі старту прибрано: на заставці його ніхто не читає,
            а знак лишається сам. Рядок повертається лише тоді, коли є що
            сказати — наприклад, що немає зв'язку з сервером. */}
        {note ? (
          <div className="splash-text">
            <span>{note}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Перемикач «увімкнено — вимкнено».
 *
 * Такий, як у телефоні: смужка з кулькою, стан видно без підпису. Ставиться
 * там, де дія одна й миттєва — увімкнув видачу рапорту в боті чи вимкнув, —
 * і кнопка «Задіяти» поруч із назвою виглядала б важче за саму дію.
 *
 * Це `button`, а не `checkbox`: усередині рядка меню, який сам є кнопкою,
 * поле форми ловило б клік на весь рядок і перемикало вкладку заразом.
 */
export function Switch({
  on,
  disabled,
  title,
  onToggle,
}: {
  on: boolean;
  disabled?: boolean;
  title?: string;
  onToggle: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      className={`switch${on ? ' on' : ''}`}
      role="switch"
      aria-checked={on}
      aria-label={title}
      title={title}
      disabled={disabled}
      onClick={e => {
        // Рядок меню під нами теж клікабельний — перемикач забирає клік собі.
        e.stopPropagation();
        onToggle();
      }}
    >
      <i />
    </button>
  );
}
